// src/components/rcv/ResumenRcvPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Grid, Button, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Divider, Alert
} from '@mui/material';
import {
  TableChartOutlined, FileDownloadOutlined, ListAltOutlined
} from '@mui/icons-material';
import { CardContainer, FormSelect, LoadingState } from '../shared';
import { apiClient } from '../../services/api';
import { formatearMonto } from '../../utils/formatters';

const ANIOS = Array.from({ length: 5 }, (_, i) => {
  const y = new Date().getFullYear() - i;
  return { value: String(y), label: String(y) };
});

const COL_C = '#1565C0';
const COL_V = '#2E7D32';

const fmtM = (val) => formatearMonto(val ?? 0);

const TH = ({ children, color, colspan = 1, align = 'center' }) => (
  <TableCell
    colSpan={colspan}
    align={align}
    sx={{
      bgcolor: color, color: '#fff', fontWeight: 'bold',
      border: '1px solid rgba(255,255,255,0.3)', py: 1, px: 1.5,
      whiteSpace: 'nowrap', fontSize: '0.75rem'
    }}
  >
    {children}
  </TableCell>
);

const TD = ({ children, bold, total }) => (
  <TableCell
    align="right"
    sx={{
      border: '1px solid #e0e0e0', py: 0.75, px: 1.5,
      fontWeight: bold ? 'bold' : 'normal',
      bgcolor: total ? '#eceff1' : 'inherit',
      fontSize: '0.8rem', whiteSpace: 'nowrap'
    }}
  >
    {children}
  </TableCell>
);

const ResumenRcvPage = () => {
  const { empresaActiva } = useSelector(s => s.empresa);
  const [anio, setAnio] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    if (!empresaActiva) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get(
        `/rcv/resumen?rut_empresa=${empresaActiva.rut}&anio=${anio}`
      );
      setData(res);
    } catch (e) {
      setError(e.message || 'Error al cargar resumen');
    } finally {
      setLoading(false);
    }
  }, [empresaActiva, anio]);

  useEffect(() => { cargar(); }, [cargar]);

  const exportar = async (tipo) => {
    if (!empresaActiva) return;
    try {
      const endpoint = `/rcv/resumen/exportar?rut_empresa=${empresaActiva.rut}&anio=${anio}&tipo=${tipo}`;
      const blob = await apiClient.get(endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RCV_${tipo}_${empresaActiva.rut}_${anio}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Error al exportar');
    }
  };

  // Totales anuales
  const totales = data.reduce((acc, m) => ({
    c_afecto:    acc.c_afecto    + m.compras.afecto,
    c_exento:    acc.c_exento    + m.compras.exento,
    c_iva_rec:   acc.c_iva_rec   + m.compras.iva_recuperable,
    c_iva_norec: acc.c_iva_norec + m.compras.iva_no_recuperable,
    c_otros:     acc.c_otros     + m.compras.otros_impuestos,
    c_total:     acc.c_total     + m.compras.total,
    v_afecto:    acc.v_afecto    + m.ventas.afecto,
    v_exento:    acc.v_exento    + m.ventas.exento,
    v_iva:       acc.v_iva       + m.ventas.iva,
    v_otros:     acc.v_otros     + m.ventas.otros_impuestos,
    v_total:     acc.v_total     + m.ventas.total,
  }), {
    c_afecto:0, c_exento:0, c_iva_rec:0, c_iva_norec:0, c_otros:0, c_total:0,
    v_afecto:0, v_exento:0, v_iva:0,     v_otros:0,     v_total:0,
  });

  const hayDatos = data.some(m =>
    m.compras.total > 0 || m.ventas.total > 0
  );

  return (
    <CardContainer
      title="Resumen Compras y Ventas"
      subheader="Resumen mensual del Registro de Compras y Ventas"
    >
      {/* Filtros */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3} md={2}>
          <FormSelect
            name="anio"
            label="Año"
            value={anio}
            onChange={e => setAnio(e.target.value)}
            options={ANIOS}
            size="small"
          />
        </Grid>
        <Grid item xs="auto">
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={() => exportar('resumen')}
            disabled={!hayDatos}
            size="small"
          >
            Exportar Resumen
          </Button>
        </Grid>
        <Grid item xs="auto">
          <Button
            variant="outlined"
            startIcon={<ListAltOutlined />}
            onClick={() => exportar('detalle')}
            disabled={!hayDatos}
            size="small"
            color="success"
          >
            Exportar Detalle
          </Button>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <LoadingState loading={loading}>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              {/* Fila 1: grupos */}
              <TableRow>
                <TH color="#37474F" colspan={2}>Período</TH>
                <TH color={COL_C} colspan={6}>COMPRAS</TH>
                <TH color={COL_V} colspan={5}>VENTAS</TH>
              </TableRow>
              {/* Fila 2: columnas */}
              <TableRow>
                <TH color="#546E7A">Período</TH>
                <TH color="#546E7A">Mes</TH>
                <TH color={COL_C}>Afecto</TH>
                <TH color={COL_C}>Exento</TH>
                <TH color={COL_C}>IVA Rec.</TH>
                <TH color={COL_C}>IVA No Rec.</TH>
                <TH color={COL_C}>Otros Imp.</TH>
                <TH color={COL_C}>Total</TH>
                <TH color={COL_V}>Afecto</TH>
                <TH color={COL_V}>Exento</TH>
                <TH color={COL_V}>IVA</TH>
                <TH color={COL_V}>Otros Imp.</TH>
                <TH color={COL_V}>Total</TH>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((m) => {
                const sinDatos = m.compras.total === 0 && m.ventas.total === 0;
                return (
                  <TableRow
                    key={m.periodo}
                    sx={{ opacity: sinDatos ? 0.4 : 1, '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    <TableCell sx={{ border:'1px solid #e0e0e0', py:0.75, fontSize:'0.8rem' }}>
                      {m.periodo}
                    </TableCell>
                    <TableCell sx={{ border:'1px solid #e0e0e0', py:0.75, fontSize:'0.8rem' }}>
                      {m.mes}
                    </TableCell>
                    <TD>{fmtM(m.compras.afecto)}</TD>
                    <TD>{fmtM(m.compras.exento)}</TD>
                    <TD>{fmtM(m.compras.iva_recuperable)}</TD>
                    <TD>{fmtM(m.compras.iva_no_recuperable)}</TD>
                    <TD>{fmtM(m.compras.otros_impuestos)}</TD>
                    <TD bold>{fmtM(m.compras.total)}</TD>
                    <TD>{fmtM(m.ventas.afecto)}</TD>
                    <TD>{fmtM(m.ventas.exento)}</TD>
                    <TD>{fmtM(m.ventas.iva)}</TD>
                    <TD>{fmtM(m.ventas.otros_impuestos)}</TD>
                    <TD bold>{fmtM(m.ventas.total)}</TD>
                  </TableRow>
                );
              })}

              {/* Fila total */}
              {data.length > 0 && (
                <TableRow sx={{ bgcolor: '#eceff1' }}>
                  <TableCell colSpan={2} sx={{ border:'1px solid #e0e0e0', fontWeight:'bold', fontSize:'0.8rem' }}>
                    TOTAL {anio}
                  </TableCell>
                  <TD bold total>{fmtM(totales.c_afecto)}</TD>
                  <TD bold total>{fmtM(totales.c_exento)}</TD>
                  <TD bold total>{fmtM(totales.c_iva_rec)}</TD>
                  <TD bold total>{fmtM(totales.c_iva_norec)}</TD>
                  <TD bold total>{fmtM(totales.c_otros)}</TD>
                  <TD bold total>{fmtM(totales.c_total)}</TD>
                  <TD bold total>{fmtM(totales.v_afecto)}</TD>
                  <TD bold total>{fmtM(totales.v_exento)}</TD>
                  <TD bold total>{fmtM(totales.v_iva)}</TD>
                  <TD bold total>{fmtM(totales.v_otros)}</TD>
                  <TD bold total>{fmtM(totales.v_total)}</TD>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!hayDatos && !loading && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <TableChartOutlined sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No hay datos para {anio}</Typography>
          </Box>
        )}
      </LoadingState>
    </CardContainer>
  );
};

export default ResumenRcvPage;
